/**
 * Editor a pantalla partida de una factura EMITIDA: documento a la izquierda,
 * formulario de datos contables a la derecha. Réplica funcional del editor de
 * recibidas, adaptado a ventas (cliente + cuenta 430 + cuenta de venta 700).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import NumericInput from "@/components/NumericInput";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  UserPlus,
  AlertTriangle,
  CheckCircle,
  X,
} from "lucide-react";
import RotatablePage from "@/components/RotatablePage";
import PdfViewerToolbar, { type FitMode } from "@/components/PdfViewerToolbar";
import { downloadFileAsZip, downloadFilesAsZip } from "@/lib/pdfDownload";
import { formatMoney, parseNumericLike } from "@/lib/numberFormatting";
import { getPdfDocument } from "@/lib/pdfjs";
import CustomerMasterPicker, { type CustomerOption } from "@/components/emitidas/CustomerMasterPicker";
import ExpenseAccountCombobox, { type CatalogEntry } from "@/components/ExpenseAccountCombobox";

import {
  docTotals,
  emptyVatLine,
  validateIssuedDoc,
  type IssuedProcessedFile,
} from "@/lib/issuedProcessing";

interface Props {
  doc: IssuedProcessedFile | null;
  index: number;
  total: number;
  onChange: (patch: Partial<IssuedProcessedFile>) => void;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onAddCustomer: () => void;
  onDelete?: () => void;
  /** Clientes del maestro de la empresa activa, para el desplegable de cuenta cliente. */
  masterOptions?: CustomerOption[];
  /** Catálogo de contrapartidas de la empresa, para el desplegable de cuenta de venta. */
  accountCatalog?: CatalogEntry[];
  companyId?: string | null;
  onAccountCreated?: (account: string, description: string) => void;
}

const IssuedInvoiceSplitView = ({ doc, index, total, onChange, onClose, onPrev, onNext, onAddCustomer, onDelete, masterOptions = [], accountCatalog = [], companyId, onAccountCreated }: Props) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState<FitMode>("width");
  const viewerRef = useRef<HTMLDivElement>(null);
  const [viewerH, setViewerH] = useState(0);
  useEffect(() => { setRotation(0); }, [doc?.id]);
  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    const upd = () => setViewerH(el.clientHeight - 16);
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, [doc?.id]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setPdfPages([]);
    setImageUrl(null);
    if (!doc) return;

    const isImg =
      doc.file.type.startsWith("image/") || /\.(png|jpe?g)$/i.test(doc.file.name);

    if (isImg) {
      setLoadingDoc(false);
      objectUrl = URL.createObjectURL(doc.file);
      setImageUrl(objectUrl);
    } else {
      setLoadingDoc(true);
      (async () => {
        try {
          const buf = await doc.file.arrayBuffer();
          if (cancelled) return;
          const pdf = await (await getPdfDocument({ data: buf })).promise;
          const pages: string[] = [];
          for (let i = 1; i <= pdf.numPages; i++) {
            if (cancelled) return;
            const page = await pdf.getPage(i);
            const vp = page.getViewport({ scale: 1.5 });
            const canvas = document.createElement("canvas");
            canvas.width = vp.width;
            canvas.height = vp.height;
            await page.render({
              canvas,
              canvasContext: canvas.getContext("2d")!,
              viewport: vp,
            }).promise;
            pages.push(canvas.toDataURL("image/png"));
            canvas.width = 0;
            canvas.height = 0;
          }
          if (!cancelled) setPdfPages(pages);
        } catch (e) {
          console.error("Error rendering issued PDF:", e);
        } finally {
          if (!cancelled) setLoadingDoc(false);
        }
      })();
    }

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.id]);


  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.altKey) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); onPrev(); }
      if (e.key === "ArrowRight") { e.preventDefault(); onNext(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onPrev, onNext]);

  const errors = useMemo(() => (doc ? validateIssuedDoc(doc) : []), [doc]);
  const totals = useMemo(() => (doc ? docTotals(doc) : null), [doc]);

  if (!doc) return null;

  const isImage = doc.file.type.startsWith("image/");

  const setLine = (i: number, patch: Partial<IssuedProcessedFile["vatLines"][number]>) => {
    const lines = doc.vatLines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    onChange({ vatLines: lines });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-[95vw] w-[95vw] h-[92vh] p-0 gap-0 overflow-hidden"
        aria-describedby={undefined}
      >
        <div className="flex h-full min-h-0 flex-col lg:flex-row">
          {/* Documento */}
          <div className="flex flex-1 min-h-[35vh] lg:min-h-0 flex-col bg-muted/40 border-b lg:border-b-0 lg:border-r">
            <div className="flex shrink-0 items-center gap-2 border-b bg-background/95 px-3 py-2">
              {pdfPages.length > 0 && (
                <span className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
                  {pdfPages.length} {pdfPages.length === 1 ? "página" : "páginas"}
                </span>
              )}
              <div className="ml-auto">
                <PdfViewerToolbar
                  onDownload={() => {
                    const att = doc.attachments ?? [];
                    const base = doc.file.name.replace(/\.[^.]+$/, "");
                    if (att.length === 0) void downloadFileAsZip(doc.file, doc.file.name);
                    else void downloadFilesAsZip([{ file: doc.file, name: doc.file.name }, ...att.map((f) => ({ file: f, name: `${base}_anexo_${f.name}` }))], `${base}.zip`);
                  }}
                  zoom={zoom}
                  onZoom={setZoom}
                  fitMode={fitMode}
                  onFitMode={setFitMode}
                  onRotate={() => setRotation((r) => (r + 270) % 360)}
                  onDelete={onDelete}
                />
              </div>
            </div>
            <div ref={viewerRef} className="relative flex-1 overflow-auto p-2 space-y-2">
              {loadingDoc && (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Cargando documento…
                </div>
              )}
              {imageUrl && (
                <RotatablePage src={imageUrl} alt={`Factura ${doc.invoiceNumber}`} rotation={rotation} zoom={zoom} fitMode={fitMode} fitHeight={viewerH} />
              )}
              {pdfPages.map((src, i) => (
                <RotatablePage key={i} src={src} alt={`Página ${i + 1} de ${doc.file.name}`} rotation={rotation} zoom={zoom} fitMode={fitMode} fitHeight={viewerH} />
              ))}
              {!loadingDoc && !imageUrl && pdfPages.length === 0 && (
                <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
                  No se puede previsualizar este documento.
                </div>
              )}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2 border-t bg-background/95 px-3 py-2">
              <span className="rounded-md bg-muted px-2 py-1 text-xs">
                {index + 1} / {total}
              </span>
              <Button size="icon" variant="secondary" className="h-8 w-8" onClick={onPrev} disabled={total <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="secondary" className="h-8 w-8" onClick={onNext} disabled={total <= 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Formulario */}
          <div className="w-full lg:w-[520px] xl:w-[600px] shrink-0 overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-background px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{doc.file.name}</p>
                {errors.length === 0 ? (
                  <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand-green))]">
                    <CheckCircle className="h-3.5 w-3.5" /> Datos completos
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" /> {errors.length} aviso(s)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>


            <div className="space-y-4 p-4">
              {errors.length > 0 && (
                <ul className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive space-y-1">
                  {errors.map((e) => <li key={e}>• {e}</li>)}
                </ul>
              )}

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Fecha</Label>
                  <Input type="date" value={doc.date} onChange={(e) => onChange({ date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Serie</Label>
                  <Input value={doc.series} onChange={(e) => onChange({ series: e.target.value })} autoComplete="off" />
                </div>
                <div>
                  <Label className="text-xs">Nº factura</Label>
                  <Input value={doc.invoiceNumber} onChange={(e) => onChange({ invoiceNumber: e.target.value })} autoComplete="off" />
                </div>
              </div>

              {doc.isCreditNote && (
                <p className="text-[11px] font-medium text-destructive">
                  Abono / rectificativa: importes en negativo
                  {doc.creditNoteHints && doc.creditNoteHints.length > 0
                    ? ` (${doc.creditNoteHints.join(", ")})`
                    : ""}
                  .
                </p>
              )}

              <div className="rounded-md border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cliente</h3>
                  <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={onAddCustomer}>
                    <UserPlus className="h-3.5 w-3.5" />
                    {doc.masterApplied ? "Editar en maestro" : "Añadir al maestro"}
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <Label className="text-xs">Nombre</Label>
                    <Input value={doc.customerName} onChange={(e) => onChange({ customerName: e.target.value })} autoComplete="off" />
                  </div>
                  <div>
                    <Label className="text-xs">NIF</Label>
                    <Input
                      value={doc.customerTaxId}
                      onChange={(e) => onChange({ customerTaxId: e.target.value.toUpperCase() })}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Cuenta cliente</Label>
                    <div className="flex gap-1">
                      <Input value={doc.customerAccount} onChange={(e) => onChange({ customerAccount: e.target.value })} autoComplete="off" />
                      <CustomerMasterPicker
                        value={doc.customerAccount}
                        options={masterOptions}
                        onSelect={(o) =>
                          onChange({
                            customerAccount: o.accountCode,
                            customerName: o.description || doc.customerName,
                            customerTaxId: o.taxId || doc.customerTaxId,
                            salesAccount: o.salesAccount || doc.salesAccount,
                            postalCode: o.postalCode || doc.postalCode,
                            masterApplied: true,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Cuenta venta</Label>
                    <div className="flex h-10 items-center rounded-md border border-input bg-background px-1">
                      <ExpenseAccountCombobox
                        value={doc.salesAccount}
                        onChange={(account) => onChange({ salesAccount: account })}
                        catalog={accountCatalog}
                        placeholder="Cuenta de venta"
                        companyId={companyId}
                        onAccountCreated={onAccountCreated}
                        triggerClassName="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">C. postal</Label>
                    <Input
                      value={doc.postalCode}
                      onChange={(e) => onChange({ postalCode: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-md border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Desglose de IVA</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 text-xs"
                    onClick={() => onChange({ vatLines: [...doc.vatLines, emptyVatLine()] })}
                  >
                    <Plus className="h-3.5 w-3.5" /> Añadir línea
                  </Button>
                </div>

                {doc.vatLines.map((line, i) => (
                  <div key={i} className="grid grid-cols-[1fr_60px_1fr_auto] items-end gap-2">
                    <div>
                      {i === 0 && <Label className="text-[10px] text-muted-foreground">Base</Label>}
                      <NumericInput
                        value={line.taxableBase}
                        onChange={(raw) => {
                          const base = parseNumericLike(raw);
                          const rate = parseNumericLike(line.vatRate);
                          setLine(i, {
                            taxableBase: base,
                            vatAmount: rate ? Number((base * rate / 100).toFixed(2)) : line.vatAmount,
                          });
                        }}
                      />
                    </div>
                    <div>
                      {i === 0 && <Label className="text-[10px] text-muted-foreground">% IVA</Label>}
                      <NumericInput
                        value={line.vatRate}
                        onChange={(raw) => {
                          const rate = parseNumericLike(raw);
                          const base = parseNumericLike(line.taxableBase);
                          setLine(i, { vatRate: rate, vatAmount: Number((base * rate / 100).toFixed(2)) });
                        }}
                      />
                    </div>
                    <div>
                      {i === 0 && <Label className="text-[10px] text-muted-foreground">Cuota</Label>}
                      <NumericInput value={line.vatAmount} onChange={(raw) => setLine(i, { vatAmount: parseNumericLike(raw) })} />
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-9 w-9 text-muted-foreground"
                      onClick={() => onChange({ vatLines: doc.vatLines.filter((_, idx) => idx !== i) })}
                      disabled={doc.vatLines.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">% IRPF</Label>
                  <NumericInput
                    value={doc.irpfRate}
                    onChange={(raw) => {
                      const rate = parseNumericLike(raw);
                      const base = doc.vatLines.reduce((s, l) => s + parseNumericLike(l.taxableBase), 0);
                      onChange({ irpfRate: rate, irpfAmount: Number((base * rate / 100).toFixed(2)) });
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs">Cuota IRPF</Label>
                  <NumericInput value={doc.irpfAmount} onChange={(raw) => onChange({ irpfAmount: parseNumericLike(raw) })} />
                </div>
                <div>
                  <Label className="text-xs">Total factura</Label>
                  <NumericInput value={doc.totalAmount} onChange={(raw) => onChange({ totalAmount: parseNumericLike(raw) })} />
                </div>
              </div>


              {totals && (
                <div className="rounded-md bg-muted/50 p-3 text-xs space-y-1">
                  <div className="flex justify-between"><span>Base</span><strong>{formatMoney(totals.base)}</strong></div>
                  <div className="flex justify-between"><span>IVA</span><strong>{formatMoney(totals.vat)}</strong></div>
                  {totals.irpf !== 0 && (
                    <div className="flex justify-between"><span>IRPF</span><strong>−{formatMoney(totals.irpf)}</strong></div>
                  )}
                  <div className="flex justify-between border-t pt-1">
                    <span>Calculado</span><strong>{formatMoney(totals.expected)}</strong>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IssuedInvoiceSplitView;
